import {
  clearClarityLoggedInSessionStorage,
  syncClarityLoggedInSessionFromApiSession
} from '@/shared/analytics/claritySession'
import { apiClient } from '@/api/apiClient'
import { USER_SESSION_QUERY_KEY } from '@/api/queryClient'
import { Session } from '@/api/models/UserModel'
import useI18n from '@/shared/hooks/usei18n'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios, { isAxiosError } from 'axios'

export { USER_SESSION_QUERY_KEY }

// API Payload Types
export type LoginPayload = {
  email: string
  password: string
}

export type SignUpPayload = {
  firstName: string
  lastName: string
  email: string
  password: string
}

export type SignUpWithInvitationPayload = SignUpPayload & {
  invitationCode: string
}

type RequestResetPasswordPayload = {
  email: string
}

type RequestMagicLinkPayload = {
  email: string
}

type ConsumeMagicLinkPayload = {
  code: string
}

type ResetPasswordPayload = {
  token: string
  password: string
}

type SsoAvailabilityResponse = {
  available: boolean
  organizationId?: string
}

type BoomError = {
  statusCode: number
  error: string
  message: string
}

export const errors = {
  USER_ALREADY_EXISTS: 'USER_ALREADY_EXISTS',
  NOT_FOUND: 'NOT_FOUND',
  UNKOWN_ERROR: 'UNKNOWN_ERROR',
  INVALID_PASSWORD_RESET_TOKEN: 'INVALID_PASSWORD_RESET_TOKEN',
  PASSWORD_RESET_TOKEN_EXPIRED: 'PASSWORD_RESET_TOKEN_EXPIRED',
  PASSWORD_RESET_TOKEN_ALREADY_USED: 'PASSWORD_RESET_TOKEN_ALREADY_USED',
  INVITATION_EMAIL_MISMATCH: 'INVITATION_EMAIL_MISMATCH'
}

// Error message constants for signup
const UNKNOWN_ERROR = 'UNKNOWN_ERROR'
const SIGNUP_ERROR_USER_ALREADY_EXISTS = 'USER_ALREADY_EXISTS'

export const useLogin = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res = await apiClient.post('/v1/auth/login', payload)
      return res.data
    }
  })
}

export function useGetUserSession() {
  return useQuery<Session | null>({
    queryKey: [USER_SESSION_QUERY_KEY],
    queryFn: async () => {
      const response = await apiClient.get<Session>('/v1/auth/session')

      syncClarityLoggedInSessionFromApiSession(response.data)

      return response.data
    },
    staleTime: Infinity,
    retry: (_failureCount, error) => {
      if (isAxiosError(error) && error.response?.status === 401) {
        return false
      }
      return true
    }
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete('/v1/auth/session')
    },
    onSuccess: () => {
      clearClarityLoggedInSessionStorage()
      queryClient.clear()
      window.location.reload()
    }
  })
}

export const useAcceptInvitation = () => {
  return useMutation({
    mutationFn: async (payload: SignUpWithInvitationPayload) => {
      try {
        const res = await apiClient.post(`/v1/auth/signup-with-invitation`, payload)
        return res.data
      } catch (error) {
        if (isAxiosError<BoomError>(error)) {
          if (
            error.response?.status === 400 &&
            error.response?.data.message === 'INVITATION_EMAIL_MISMATCH'
          ) {
            throw new Error(errors.INVITATION_EMAIL_MISMATCH)
          }
        }
        throw error
      }
    }
  })
}

export const useSignup = () => {
  return useMutation<{ success: boolean; error?: string }, Error, SignUpPayload>({
    mutationFn: async (payload: SignUpPayload) => {
      try {
        await apiClient.post('/v1/auth/signup', payload)
        return {
          success: true
        }
      } catch (error) {
        if (axios.isAxiosError<BoomError>(error)) {
          if (error.response?.status === 409) {
            return {
              success: false,
              error: SIGNUP_ERROR_USER_ALREADY_EXISTS
            }
          }

          return {
            success: false,
            error: error.response?.data.message || UNKNOWN_ERROR
          }
        }

        return {
          success: false,
          error: error instanceof Error ? error.message : UNKNOWN_ERROR
        }
      }
    }
  })
}

export const useRequestResetPassword = () => {
  return useMutation({
    mutationFn: async (payload: RequestResetPasswordPayload) => {
      const res = await apiClient.post('/v1/auth/request-password-reset', payload)
      return res.data
    }
  })
}

export const useRequestMagicLink = () => {
  const { currentLanguage } = useI18n()
  return useMutation({
    mutationFn: async (payload: RequestMagicLinkPayload) => {
      const res = await apiClient.post('/v1/auth/request-magic-link', {
        ...payload,
        locale: currentLanguage
      })
      return res.data
    }
  })
}

export const useConsumeMagicLink = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: ConsumeMagicLinkPayload) => {
      try {
        const res = await apiClient.post<{ redirectPath: string }>(
          '/v1/auth/magic-login',
          payload
        )
        return res.data
      } catch (error) {
        if (isAxiosError<BoomError>(error)) {
          const message = error.response?.data.message
          if (
            message === 'INVALID_LINK' ||
            message === 'EXPIRED_LINK' ||
            message === 'INVALID_REQUEST' ||
            message === 'UNKNOWN_ERROR'
          ) {
            throw new Error(message)
          }
        }
        throw new Error('UNKNOWN_ERROR')
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [USER_SESSION_QUERY_KEY] })
    }
  })
}

export const useResetPassword = () => {
  return useMutation({
    mutationFn: async (payload: ResetPasswordPayload) => {
      try {
        const res = await apiClient.post('/v1/auth/reset-password', payload)
        return res.data
      } catch (error) {
        if (axios.isAxiosError<BoomError>(error)) {
          if (
            error.response?.status === 400 &&
            error.response?.data.message === 'INVALID_PASSWORD_RESET_TOKEN'
          ) {
            throw new Error(errors.INVALID_PASSWORD_RESET_TOKEN)
          }
          if (
            error.response?.status === 400 &&
            error.response?.data.message === 'PASSWORD_RESET_TOKEN_EXPIRED'
          ) {
            throw new Error(errors.PASSWORD_RESET_TOKEN_EXPIRED)
          }
          if (
            error.response?.status === 400 &&
            error.response?.data.message === 'PASSWORD_RESET_TOKEN_ALREADY_USED'
          ) {
            throw new Error(errors.PASSWORD_RESET_TOKEN_ALREADY_USED)
          }
        }
        throw new Error(UNKNOWN_ERROR)
      }
    }
  })
}

export const useCheckSsoAvailability = () => {
  return useMutation<SsoAvailabilityResponse, Error, string>({
    mutationFn: async (email: string) => {
      const response = await apiClient.get<SsoAvailabilityResponse>('/v1/auth/sso/check', {
        params: { email }
      })
      return response.data
    }
  })
}
